// ProfessionalLoadingSystem.js
// Enterprise-grade loading states and progress indicators
// Provides consistent loading experiences across all scenes

import { MobileDetectionService } from './MobileDetectionService.js';
import { professionalAnimations } from './ProfessionalAnimationSystem.js';

/**
 * Professional loading system
 * Provides consistent, animated loading states and progress indicators
 * Optimized for both mobile and desktop experiences
 */
export class ProfessionalLoadingSystem {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent loading management
     */
    static getInstance() {
        if (!ProfessionalLoadingSystem._instance) {
            ProfessionalLoadingSystem._instance = new ProfessionalLoadingSystem();
        }
        return ProfessionalLoadingSystem._instance;
    }
    
    constructor() {
        if (ProfessionalLoadingSystem._instance) {
            return ProfessionalLoadingSystem._instance;
        }
        
        this.activeLoaders = new Map();
        this.loaderCounter = 0;
        
        this._injectLoadingCSS();
        
        ProfessionalLoadingSystem._instance = this;
    }
    
    /**
     * Show loading overlay for scene transitions
     * @param {string} message - Loading message
     * @param {Object} options - Configuration options
     * @returns {string} - Loader ID for dismissal
     */
    showSceneLoader(message = 'Loading...', options = {}) {
        const config = {
            overlay: true,
            spinner: 'dots',
            position: 'center',
            backdrop: true,
            ...options
        };
        
        const loaderId = `scene-loader-${++this.loaderCounter}`;
        const isMobile = MobileDetectionService.isMobile();
        
        console.log(`⏳ ProfessionalLoadingSystem: Showing scene loader (${isMobile ? 'mobile' : 'desktop'})`);
        
        const loader = this._createSceneLoader(loaderId, message, config, isMobile);
        document.body.appendChild(loader);
        
        // Animate in
        this._animateLoaderIn(loader);
        
        this.activeLoaders.set(loaderId, { element: loader, type: 'scene', config });
        
        return loaderId;
    }
    
    /**
     * Show inline loading state for content areas
     * @param {Element} container - Container element to show loading in
     * @param {string} message - Loading message
     * @param {Object} options - Configuration options
     * @returns {string} - Loader ID for dismissal
     */
    showInlineLoader(container, message = 'Loading...', options = {}) {
        if (!container) return null;
        
        const config = {
            spinner: 'pulse',
            skeleton: false,
            ...options
        };
        
        const loaderId = `inline-loader-${++this.loaderCounter}`;
        const isMobile = MobileDetectionService.isMobile();
        
        console.log(`⏳ ProfessionalLoadingSystem: Showing inline loader in ${container.tagName}`);
        
        const loader = this._createInlineLoader(loaderId, message, config, isMobile);
        
        // Store original content
        const originalContent = container.innerHTML;
        container.innerHTML = '';
        container.appendChild(loader);
        
        this.activeLoaders.set(loaderId, { 
            element: loader, 
            container, 
            originalContent, 
            type: 'inline', 
            config 
        });
        
        return loaderId;
    }
    
    /**
     * Show skeleton loading for content
     * @param {Element} container - Container element
     * @param {Object} options - Skeleton configuration
     * @returns {string} - Loader ID for dismissal
     */
    showSkeleton(container, options = {}) {
        if (!container) return null;
        
        const config = {
            rows: 3,
            columns: 1,
            showAvatar: false,
            showImage: false,
            ...options
        };
        
        const loaderId = `skeleton-loader-${++this.loaderCounter}`;
        
        console.log(`💀 ProfessionalLoadingSystem: Showing skeleton loader`);
        
        const skeleton = this._createSkeletonLoader(loaderId, config);
        
        // Store original content
        const originalContent = container.innerHTML;
        container.innerHTML = '';
        container.appendChild(skeleton);
        
        this.activeLoaders.set(loaderId, { 
            element: skeleton, 
            container, 
            originalContent, 
            type: 'skeleton', 
            config 
        });
        
        return loaderId;
    }
    
    /**
     * Update loading progress
     * @param {string} loaderId - Loader ID
     * @param {number} percentage - Progress percentage (0-100)
     * @param {string} message - Optional message update
     */
    updateProgress(loaderId, percentage, message = null) {
        const loader = this.activeLoaders.get(loaderId);
        if (!loader) return;
        
        const progressBar = loader.element.querySelector('.loading-progress-bar');
        const messageEl = loader.element.querySelector('.loading-message');
        
        if (progressBar) {
            professionalAnimations.animateProgress(progressBar.parentElement, percentage);
        }
        
        if (message && messageEl) {
            messageEl.textContent = message;
        }
        
        console.log(`📊 Loading progress updated: ${loaderId} - ${percentage}%`);
    }
    
    /**
     * Hide loading state
     * @param {string} loaderId - Loader ID to hide
     * @param {Object} options - Hide options
     */
    hide(loaderId, options = {}) {
        const loader = this.activeLoaders.get(loaderId);
        if (!loader) return;
        
        const config = {
            delay: 0,
            restoreContent: true,
            ...options
        };
        
        console.log(`⏳ ProfessionalLoadingSystem: Hiding loader ${loaderId}`);
        
        setTimeout(() => {
            this._hideLoader(loader, loaderId, config);
        }, config.delay);
    }
    
    /**
     * Hide all active loaders
     */
    hideAll() {
        console.log('⏳ ProfessionalLoadingSystem: Hiding all loaders');
        
        const loaderIds = Array.from(this.activeLoaders.keys());
        loaderIds.forEach(id => this.hide(id));
    }
    
    /**
     * Create scene loader element
     * @private
     */
    _createSceneLoader(id, message, config, isMobile) {
        const loader = document.createElement('div');
        loader.id = id;
        loader.className = 'professional-scene-loader';
        
        if (isMobile) {
            loader.classList.add('loader-mobile');
        }
        
        const spinnerHTML = this._getSpinnerHTML(config.spinner);
        
        loader.innerHTML = `
            ${config.backdrop ? '<div class="loader-backdrop"></div>' : ''}
            <div class="loader-content">
                <div class="loader-spinner">${spinnerHTML}</div>
                <div class="loading-message">${message}</div>
                ${config.showProgress ? '<div class="loading-progress"><div class="loading-progress-bar"></div></div>' : ''}
            </div>
        `;
        
        return loader;
    }
    
    /**
     * Create inline loader element
     * @private
     */
    _createInlineLoader(id, message, config, isMobile) {
        const loader = document.createElement('div');
        loader.id = id;
        loader.className = 'professional-inline-loader';
        
        if (isMobile) {
            loader.classList.add('loader-mobile');
        }
        
        const spinnerHTML = this._getSpinnerHTML(config.spinner);
        
        loader.innerHTML = `
            <div class="inline-loader-content">
                <div class="loader-spinner">${spinnerHTML}</div>
                <div class="loading-message">${message}</div>
            </div>
        `;
        
        return loader;
    }
    
    /**
     * Create skeleton loader element
     * @private
     */
    _createSkeletonLoader(id, config) {
        const skeleton = document.createElement('div');
        skeleton.id = id;
        skeleton.className = 'professional-skeleton-loader';
        
        let skeletonHTML = '';
        
        if (config.showAvatar) {
            skeletonHTML += '<div class="skeleton-avatar"></div>';
        }
        
        if (config.showImage) {
            skeletonHTML += '<div class="skeleton-image"></div>';
        }
        
        // Create skeleton rows
        for (let i = 0; i < config.rows; i++) {
            skeletonHTML += `
                <div class="skeleton-row">
                    ${Array(config.columns).fill('<div class="skeleton-item"></div>').join('')}
                </div>
            `;
        }
        
        skeleton.innerHTML = `<div class="skeleton-content">${skeletonHTML}</div>`;
        
        return skeleton;
    }
    
    /**
     * Get spinner HTML based on type
     * @private
     */
    _getSpinnerHTML(spinnerType) {
        switch (spinnerType) {
            case 'dots':
                return `
                    <div class="spinner-dots">
                        <div class="dot"></div>
                        <div class="dot"></div>
                        <div class="dot"></div>
                    </div>
                `;
            case 'circle':
                return '<div class="spinner-circle"></div>';
            case 'pulse':
                return '<div class="spinner-pulse"></div>';
            case 'bars':
                return `
                    <div class="spinner-bars">
                        <div class="bar"></div>
                        <div class="bar"></div>
                        <div class="bar"></div>
                        <div class="bar"></div>
                    </div>
                `;
            default:
                return '<div class="spinner-circle"></div>';
        }
    }
    
    /**
     * Animate loader in
     * @private
     */
    _animateLoaderIn(loader) {
        const duration = professionalAnimations.getDuration('fast');
        
        loader.style.opacity = '0';
        loader.style.transform = 'scale(0.9)';
        
        requestAnimationFrame(() => {
            loader.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
            loader.style.opacity = '1';
            loader.style.transform = 'scale(1)';
        });
    }
    
    /**
     * Hide loader with animation
     * @private
     */
    _hideLoader(loader, loaderId, config) {
        const duration = professionalAnimations.getDuration('fast');
        
        // Animate out
        loader.element.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        loader.element.style.opacity = '0';
        loader.element.style.transform = 'scale(0.9)';
        
        setTimeout(() => {
            // Restore original content for inline loaders
            if (loader.type === 'inline' || loader.type === 'skeleton') {
                if (config.restoreContent && loader.container && loader.originalContent) {
                    loader.container.innerHTML = loader.originalContent;
                }
            }
            
            // Remove loader element
            if (loader.element.parentNode) {
                loader.element.parentNode.removeChild(loader.element);
            }
            
            // Remove from tracking
            this.activeLoaders.delete(loaderId);
            
            console.log(`✅ Loader hidden: ${loaderId}`);
        }, duration);
    }
    
    /**
     * Inject loading CSS
     * @private
     */
    _injectLoadingCSS() {
        if (document.getElementById('professional-loading-css')) {
            return; // Already injected
        }
        
        const loadingCSS = `
            <style id="professional-loading-css">
                /* Professional Loading System - Enterprise Grade */
                
                .professional-scene-loader {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    z-index: 50000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .loader-backdrop {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(15, 23, 42, 0.8);
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                }
                
                .loader-content {
                    position: relative;
                    background: rgba(30, 41, 59, 0.9);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                    padding: 32px;
                    text-align: center;
                    min-width: 200px;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
                }
                
                .professional-inline-loader {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 40px;
                    text-align: center;
                }
                
                .inline-loader-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                }
                
                .loading-message {
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 16px;
                    font-weight: 500;
                    margin-top: 16px;
                }
                
                .loading-progress {
                    width: 200px;
                    height: 4px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 2px;
                    overflow: hidden;
                    margin-top: 16px;
                }
                
                .loading-progress-bar {
                    height: 100%;
                    background: linear-gradient(90deg, #00d4ff 0%, #0ea5e9 100%);
                    border-radius: 2px;
                    width: 0%;
                    transition: width 300ms ease;
                }
                
                /* Spinner Animations */
                .spinner-dots {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }
                
                .spinner-dots .dot {
                    width: 12px;
                    height: 12px;
                    background: #00d4ff;
                    border-radius: 50%;
                    animation: dotPulse 1.4s ease-in-out infinite both;
                }
                
                .spinner-dots .dot:nth-child(1) { animation-delay: -0.32s; }
                .spinner-dots .dot:nth-child(2) { animation-delay: -0.16s; }
                .spinner-dots .dot:nth-child(3) { animation-delay: 0s; }
                
                @keyframes dotPulse {
                    0%, 80%, 100% {
                        transform: scale(0.8);
                        opacity: 0.5;
                    }
                    40% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
                
                .spinner-circle {
                    width: 40px;
                    height: 40px;
                    border: 3px solid rgba(255, 255, 255, 0.1);
                    border-top: 3px solid #00d4ff;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .spinner-pulse {
                    width: 40px;
                    height: 40px;
                    background: #00d4ff;
                    border-radius: 50%;
                    animation: pulse 1.5s ease-in-out infinite;
                }
                
                @keyframes pulse {
                    0%, 100% {
                        transform: scale(0.8);
                        opacity: 0.5;
                    }
                    50% {
                        transform: scale(1.2);
                        opacity: 1;
                    }
                }
                
                .spinner-bars {
                    display: flex;
                    gap: 4px;
                    align-items: end;
                    height: 40px;
                }
                
                .spinner-bars .bar {
                    width: 6px;
                    background: #00d4ff;
                    border-radius: 3px;
                    animation: barPulse 1.2s ease-in-out infinite;
                }
                
                .spinner-bars .bar:nth-child(1) { animation-delay: 0s; }
                .spinner-bars .bar:nth-child(2) { animation-delay: 0.1s; }
                .spinner-bars .bar:nth-child(3) { animation-delay: 0.2s; }
                .spinner-bars .bar:nth-child(4) { animation-delay: 0.3s; }
                
                @keyframes barPulse {
                    0%, 100% {
                        height: 10px;
                        opacity: 0.5;
                    }
                    50% {
                        height: 40px;
                        opacity: 1;
                    }
                }
                
                /* Skeleton Loading */
                .professional-skeleton-loader {
                    padding: 20px;
                }
                
                .skeleton-content {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                
                .skeleton-avatar {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: linear-gradient(90deg, 
                        rgba(255, 255, 255, 0.1) 0%, 
                        rgba(255, 255, 255, 0.2) 50%, 
                        rgba(255, 255, 255, 0.1) 100%);
                    background-size: 200% 100%;
                    animation: shimmer 2s infinite linear;
                    margin-bottom: 16px;
                }
                
                .skeleton-image {
                    width: 100%;
                    height: 200px;
                    border-radius: 12px;
                    background: linear-gradient(90deg, 
                        rgba(255, 255, 255, 0.1) 0%, 
                        rgba(255, 255, 255, 0.2) 50%, 
                        rgba(255, 255, 255, 0.1) 100%);
                    background-size: 200% 100%;
                    animation: shimmer 2s infinite linear;
                    margin-bottom: 16px;
                }
                
                .skeleton-row {
                    display: flex;
                    gap: 12px;
                }
                
                .skeleton-item {
                    flex: 1;
                    height: 20px;
                    border-radius: 6px;
                    background: linear-gradient(90deg, 
                        rgba(255, 255, 255, 0.1) 0%, 
                        rgba(255, 255, 255, 0.2) 50%, 
                        rgba(255, 255, 255, 0.1) 100%);
                    background-size: 200% 100%;
                    animation: shimmer 2s infinite linear;
                }
                
                .skeleton-item:nth-child(1) { animation-delay: 0s; }
                .skeleton-item:nth-child(2) { animation-delay: 0.2s; }
                .skeleton-item:nth-child(3) { animation-delay: 0.4s; }
                
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                
                /* Mobile optimizations */
                .loader-mobile .loader-content {
                    padding: 24px;
                    min-width: 160px;
                    margin: 0 20px;
                }
                
                .loader-mobile .loading-message {
                    font-size: 14px;
                }
                
                /* Accessibility */
                @media (prefers-reduced-motion: reduce) {
                    .professional-scene-loader *,
                    .professional-inline-loader *,
                    .professional-skeleton-loader * {
                        animation: none !important;
                        transition: none !important;
                    }
                    
                    .spinner-circle {
                        border-top-color: #00d4ff;
                        opacity: 0.8;
                    }
                }
            </style>
        `;
        
        document.head.insertAdjacentHTML('beforeend', loadingCSS);
        console.log('🎨 Professional loading system CSS injected');
    }
    
    /**
     * Get spinner HTML based on type
     * @private
     */
    _getSpinnerHTML(type) {
        switch (type) {
            case 'dots':
                return `
                    <div class="spinner-dots">
                        <div class="dot"></div>
                        <div class="dot"></div>
                        <div class="dot"></div>
                    </div>
                `;
            case 'circle':
                return '<div class="spinner-circle"></div>';
            case 'pulse':
                return '<div class="spinner-pulse"></div>';
            case 'bars':
                return `
                    <div class="spinner-bars">
                        <div class="bar"></div>
                        <div class="bar"></div>
                        <div class="bar"></div>
                        <div class="bar"></div>
                    </div>
                `;
            default:
                return '<div class="spinner-circle"></div>';
        }
    }
    
    /**
     * Get active loader count
     * @returns {number} - Number of active loaders
     */
    getActiveLoaderCount() {
        return this.activeLoaders.size;
    }
    
    /**
     * Check if a specific loader is active
     * @param {string} loaderId - Loader ID to check
     * @returns {boolean} - Whether loader is active
     */
    isLoaderActive(loaderId) {
        return this.activeLoaders.has(loaderId);
    }
}

// Export singleton instance
export const professionalLoading = ProfessionalLoadingSystem.getInstance();
export default professionalLoading;





